import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import classes from "./SetToOutMailTable.module.scss";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DoDisturbOnIcon from "@mui/icons-material/DoDisturbOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  getFilteredRowModel,
  getPaginationRowModel,
  ColumnFiltersState,
  SortingState,
  ColumnDef,
  FilterFn,
} from "@tanstack/react-table";
import {
  TiArrowSortedUp,
  TiArrowSortedDown,
  TiArrowUnsorted,
} from "react-icons/ti";
import Pagination from "./Pagination";
import { AddressInfoType, User } from "../../../../types";
import EditOutsourcingMailModal from "../modal/EditOutsourcingMailModal";
import ConfirmModal from "../../../modal/confirm/ConfirmModal";
import { useDeleteAddressList } from "../../../../hooks/api/address/useDeleteAddress";
import SelectBoxForNumber from "../../../uikit/atoms/selectboxes/SelectBoxForNumber";
import { factoriesForSelect } from "../../../../utils/variables/factoriesForInupt";
import { useRecoilState } from "recoil";
import { userState } from "../../../../states/userState";
import { setUserToLocalStorage } from "../../../../utils/functions/localStorageManager";
import EditCcMailModal from "../modal/EditCcMailModal";

// type User = {
//   id: string;
//   workerId: string;
//   workerName: string;
//   factoryNo: string;
//   factoryName: string;
//   address: string;
// };

// const generateDummyData = (count: number): User[] => {
//   const dummyData: User[] = [];
//   for (let i = 1; i <= count; i++) {
//     dummyData.push({
//       id: `user-${i}`,
//       workerId: `W${i.toString().padStart(3, "0")}`,
//       workerName: `作業者 ${i}`,
//       factoryNo: `F${Math.ceil(i / 10)}`,
//       factoryName: `工場 ${Math.ceil(i / 10)}`,
//       address: `user${i}@example.com`,
//     });
//   }
//   return dummyData;
// };
const factoryIdFilter: FilterFn<AddressInfoType> = (
  row,
  columnId,
  filterValue
) => {
  const rowValue = row.getValue(columnId);
  if (!rowValue) return false;

  // Convert both values to strings and to the same case for case-insensitive matching
  const rowValueStr = String(rowValue).toLowerCase();
  const filterValueStr = String(filterValue).toLowerCase();

  return rowValueStr.includes(filterValueStr);
};
const isSelectedFilter: FilterFn<AddressInfoType> = (
  row,
  columnId,
  filterValue
) => {
  if (filterValue === "") return true;

  const rowValue = row.getValue(columnId);
  if (filterValue === "true") {
    return rowValue === true;
  } else if (filterValue === "false") {
    return rowValue === false;
  }

  return true;
};

type Props = {
  getAddressList: (address: string, factoryId: string) => Promise<any>;
  setAddressList: React.Dispatch<React.SetStateAction<AddressInfoType[]>>;
  addressList: AddressInfoType[];
};

const SetCcMailTable: FC<Props> = (props) => {
  const { addressList, getAddressList, setAddressList } = props;
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pageSize, setPageSize] = useState(15);
  const [pageIndex, setPageIndex] = useState(0);
  const [editAddress, setEditAddress] = useState<AddressInfoType | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isShowDialog, setIsShowDialog] = useState(false);
  const [isShowDeleteDialog, setIsShowDeleteDialog] = useState(false);
  const [adress, setAddress] = useState<AddressInfoType[]>([]);
  const [user, setUser] = useRecoilState<User>(userState);
  const [factoryId, setFactoryId] = useState("");
  const { deleteCcAddress } = useDeleteAddressList();
  // 初期データを設定
  useEffect(() => {
    setAddress(addressList);
  }, [addressList]);

  const handleEditClick = useCallback(
    (address: AddressInfoType) => {
      if (!address) return;
      setIsShowDialog(true);
      setEditAddress(address);
    },
    [setIsShowDialog, setEditAddress]
  );

  const handleDeleteClick = useCallback((workerId: string) => {
    setIsShowDeleteDialog(true);
    setDeleteId(workerId);
  }, []);

  const handleDeleteAddress = useCallback(async () => {
    if (deleteId) {
      //削除処理
      try {
        await deleteCcAddress(deleteId);
        const res = await getAddressList("", "");
        setAddressList(res);
        setDeleteId(null);
        setIsShowDeleteDialog(false);
      } catch (e) {
        window.alert("エラーが発生しました" + e);
      }
    }
  }, [deleteId]);

  const columns = useMemo<ColumnDef<AddressInfoType, any>[]>(
    () => [
      {
        id: "edit",
        header: "",
        cell: ({ row }) => (
          <button
            className={classes.icon}
            onClick={() => handleEditClick(row.original)}
          >
            <EditIcon />
          </button>
        ),
        enableSorting: false,
        size: 50,
      },
      {
        id: "workerId",
        accessorKey: "workerId",
        header: "作業者番号",
        cell: ({ getValue }) => (
          <div className={classes.workerId}>{getValue()}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: "workerName",
        accessorKey: "workerName",
        header: "氏名",
        cell: ({ getValue }) => (
          <div className={classes.workerName}>{getValue()}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: "isSelected",
        accessorKey: "isSelected",
        header: ({ header }) => (
          <div className={classes.headerCell}>
            <div className={classes.headerContent}>
              <span>セット</span>
              {header.column.getCanSort() &&
                (header.column.getIsSorted() === "asc" ? (
                  <TiArrowSortedUp />
                ) : header.column.getIsSorted() === "desc" ? (
                  <TiArrowSortedDown />
                ) : (
                  <TiArrowUnsorted />
                ))}
            </div>
            {header.column.getCanFilter() && (
              <div className={classes.headerFilter}>
                <select
                  value={(header.column.getFilterValue() ?? "") as string}
                  onChange={(e) => header.column.setFilterValue(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <option value="">すべて</option>
                  <option value="true">セット済</option>
                  <option value="false">未セット</option>
                </select>
              </div>
            )}
          </div>
        ),
        cell: ({ getValue }) => (
          <div className={classes.isSelected}>
            {getValue() ? (
              <CheckCircleIcon style={{ fontSize: "20px", color: "#01f81c" }} />
            ) : (
              <DoDisturbOnIcon style={{ fontSize: "20px", color: "#ff0052" }} />
            )}
          </div>
        ),
        filterFn: isSelectedFilter,
        enableColumnFilter: true,
      },

      {
        id: "address",
        accessorKey: "address",
        header: "メールアドレス",
        cell: ({ getValue }) => (
          <div className={classes.address}>{getValue()}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: "factoryId",
        accessorKey: "factoryId",
        header: "工場",
        cell: ({ getValue }) => (
          <div className={classes.factoryNo}>{getValue()}</div>
        ),
        enableColumnFilter: true,
        filterFn: factoryIdFilter, // カスタムフィルタを指定
      },
      {
        id: "factoryName",
        accessorKey: "factoryName",
        header: "工場名",
        cell: ({ getValue }) => (
          <div className={classes.factoryName}>{getValue()}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: "delete",
        header: "",
        cell: ({ row }) => (
          <button
            className={classes.icon}
            onClick={() => handleDeleteClick(row.original.workerId)}
          >
            <DeleteIcon />
          </button>
        ),
        enableSorting: false,
        size: 50,
      },
    ],
    [handleEditClick, handleDeleteClick]
  );

  const table = useReactTable<AddressInfoType>({
    data: addressList,
    columns,
    state: {
      sorting,
      globalFilter,
      pagination: {
        pageSize,
        pageIndex,
      },
      columnFilters,
    },
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === "function"
          ? updater({ pageSize, pageIndex })
          : updater;
      setPageSize(newPagination.pageSize);
      setPageIndex(newPagination.pageIndex);
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    filterFns: {
      factoryIdFilter,
      isSelectedFilter,
    },
  });
  //工場番号入力
  const handleFactoryChange = (e: number) => {
    const factoryNo = e === 0 ? "" : e.toString();
    setFactoryId(factoryNo);

    setUser((prevUser: User) => {
      const newUser: User = {
        ...prevUser,
        initialParameters: {
          ...prevUser.initialParameters,
          factoryNoForOutsourcingCc: e,
        },
      };
      setUserToLocalStorage(newUser);
      return newUser;
    });
    // フィルタの更新
    if (factoryNo === "") {
      setColumnFilters([]); // フィルタ条件をリセット
    } else {
      setColumnFilters([{ id: "factoryId", value: factoryNo }]);
    }
  };
  useEffect(() => {
    const factoryNo = user.initialParameters.factoryNoForOutsourcingCc;
    const factoryNoStr = factoryNo === 0 ? "" : factoryNo.toString();

    setFactoryId(factoryNoStr);

    // 初期値でフィルタを適用
    if (factoryNoStr === "") {
      setColumnFilters([]); // フィルタ条件をリセット
    } else {
      setColumnFilters([{ id: "factoryId", value: factoryNoStr }]);
    }
  }, [user.initialParameters.factoryNoForOutsourcingCc]);
  return (
    <div className={classes.root}>
      <div className={classes.menu}>
        {/* <div className={classes.filter}>
          <input
            value={globalFilter ?? ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder="検索..."
            className={classes.filterInput}
          />
        </div> */}
        <div className={classes.factoryArea}>
          <p>登録工場</p>
          <SelectBoxForNumber
            label={""}
            options={factoriesForSelect}
            required={true}
            select={handleFactoryChange}
            value={factoryId === "" ? 0 : parseInt(factoryId)}
            disabled={false}
            width={"85px"}
            height={"38px"}
            fontSize={"12px"}
            colorMode={"dark"}
            setMode={"input"}
          />
        </div>
        {/* Paginationコンポーネント */}
        <Pagination
          table={table}
          pageSize={pageSize}
          setPageSize={setPageSize}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
        />
      </div>
      <div className={classes.container}>
        <table className={classes.table}>
          <thead className={classes.thead}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={
                      header.column.getCanSort()
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                    style={{
                      cursor: header.column.getCanSort()
                        ? "pointer"
                        : "default",
                      width: header.getSize(),
                      minWidth: header.column.columnDef.minSize,
                      maxWidth: header.column.columnDef.maxSize,
                      position: "relative",
                    }}
                  >
                    <div className={classes.headerCell}>
                      <div className={classes.headerContent}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getCanSort() &&
                          header.column.columnDef.id !== "isSelected" &&
                          (header.column.getIsSorted() === "asc" ? (
                            <TiArrowSortedUp />
                          ) : header.column.getIsSorted() === "desc" ? (
                            <TiArrowSortedDown />
                          ) : (
                            <TiArrowUnsorted />
                          ))}
                      </div>
                      {header.column.getCanFilter() &&
                        header.column.columnDef.id !== "isSelected" && (
                          <div className={classes.headerFilter}>
                            <input
                              value={
                                (header.column.getFilterValue() ?? "") as string
                              }
                              onChange={(e) =>
                                header.column.setFilterValue(e.target.value)
                              }
                              placeholder="フィルタ..."
                              className={classes.filterInput}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {isShowDialog && editAddress && (
        <EditCcMailModal
          address={editAddress}
          open={isShowDialog}
          setOpen={setIsShowDialog}
          getAddressList={getAddressList}
          setAddressList={setAddressList}
        />
      )}
      {isShowDeleteDialog && (
        <ConfirmModal
          setIsShow={setIsShowDeleteDialog}
          title="確認"
          message={"元に戻せません。よろしいですか？"}
          onExec={handleDeleteAddress}
          onCancel={() => setIsShowDeleteDialog(false)}
        />
      )}
    </div>
  );
};

export default SetCcMailTable;
